import { el } from "../util/dom";
import { ApiError } from "../api/client";
import {
  adminAccountAction,
  getAdminAccount,
  getAdminStats,
  listAdminAccounts,
  listAdminItems,
  type AdminAccountDetail,
  type AdminAction,
  type AdminListKind,
  type AdminStats,
  type Row,
} from "../api/admin";

export interface AdminScreen {
  title: string;
  back: string;
  view: HTMLElement;
}
export interface AdminView {
  /** The screen for an /admin path (fresh fetch every time), or null. */
  screenFor(path: string): AdminScreen | null;
}

const ACCOUNT_PATH = /^\/admin\/accounts\/(\d+)$/;

function cell(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Plain table over whatever columns the rows carry (union of keys). Scrolls
 * inside its own wrapper so a wide table never widens the card on a phone. */
export function buildTable(rows: Row[], onRow?: (row: Row) => void): HTMLElement {
  if (rows.length === 0) return el("div", { class: "no-alerts" }, ["Nothing yet."]);
  const cols = [...new Set(rows.flatMap(Object.keys))];
  const body = el(
    "tbody",
    {},
    rows.map((row) => {
      const tr = el("tr", onRow ? { class: "admin-row-link" } : {}, cols.map((c) => el("td", {}, [cell(row[c])])));
      if (onRow) tr.addEventListener("click", () => onRow(row));
      return tr;
    })
  );
  const head = el("thead", {}, [el("tr", {}, cols.map((c) => el("th", {}, [c])))]);
  return el("div", { class: "admin-table-wrap" }, [el("table", { class: "admin-table" }, [head, body])]);
}

function labeledRows(obj: Row): HTMLElement {
  return el(
    "div",
    { class: "device-list" },
    Object.entries(obj).map(([k, v]) =>
      el("div", { class: "device-row" }, [el("span", { class: "device-meta" }, [k]), el("span", {}, [cell(v)])])
    )
  );
}

function section(title: string, ...body: HTMLElement[]): HTMLElement[] {
  return [el("div", { class: "section-title" }, [title]), ...body];
}

function menuLinks(items: [string, string][]): HTMLElement {
  return el(
    "div",
    {},
    items.map(([label, href]) =>
      el("a", { class: "menu-row", href, "data-route": "" }, [label, el("span", { "aria-hidden": "true" }, ["›"])])
    )
  );
}

/** SW-29 admin screens. `navigate` is main.ts's pushState navigation; every
 * admin fetch that comes back 401/403 lands on /account through it. */
export function buildAdminView(navigate: (path: string) => void): AdminView {
  const root = el("div", { class: "auth-panel" });
  // Bumped on every screen show so a slow response from the previous screen
  // can't paint over the current one.
  let generation = 0;

  function describeError(err: unknown): string {
    if (err instanceof ApiError) return err.detail;
    return err instanceof Error ? err.message : String(err);
  }
  function denied(err: unknown): boolean {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      navigate("/account");
      return true;
    }
    return false;
  }

  async function load<T>(target: HTMLElement, fetcher: () => Promise<T>, render: (data: T) => Node[]): Promise<void> {
    const gen = generation;
    target.replaceChildren(el("p", { class: "auth-forgot-status" }, ["Loading…"]));
    try {
      const data = await fetcher();
      if (gen === generation) target.replaceChildren(...render(data));
    } catch (err) {
      if (gen !== generation || denied(err)) return;
      target.replaceChildren(el("p", { class: "auth-error" }, [describeError(err)]));
    }
  }

  function renderStats(s: AdminStats): Node[] {
    const { new_users_by_day, errors_by_day, active_subscriptions_by_platform, cancellation_reasons, funnel, ...scalars } = s;
    return [
      menuLinks([
        ["ACCOUNTS", "/admin/accounts"],
        ["REPORTS", "/admin/reports"],
        ["ERRORS", "/admin/errors"],
        ["AUDIT", "/admin/audit"],
      ]),
      ...section("Stats · last 7 days", labeledRows(scalars)),
      ...section("Active subscriptions", labeledRows(active_subscriptions_by_platform ?? {})),
      ...section("New users by day", buildTable(new_users_by_day ?? [])),
      ...section("Errors by day", buildTable(errors_by_day ?? [])),
      ...section("Cancellation reasons", buildTable(cancellation_reasons ?? [])),
      ...section("Funnel", labeledRows(funnel ?? {})),
    ];
  }

  function showAccounts(): void {
    const input = el("input", { type: "search", placeholder: "email contains…", class: "auth-input" }) as HTMLInputElement;
    const searchBtn = el("button", { class: "auth-btn" }, ["SEARCH"]);
    const results = el("div");
    const search = (): Promise<void> =>
      load(
        results,
        () => listAdminAccounts(input.value.trim()),
        (r) => [buildTable(r.accounts, (row) => navigate(`/admin/accounts/${row.id}`))]
      );
    searchBtn.addEventListener("click", () => void search());
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void search();
    });
    root.replaceChildren(el("div", { class: "auth-row" }, [input, searchBtn]), results);
    void search();
  }

  /** The five write actions, each behind a confirm step; DELETE also needs
   * the account email typed. A completed action reloads the detail (whose
   * `audit` list is where the new admin_audit row shows up). */
  function actionsFor(d: AdminAccountDetail, reload: () => void): HTMLElement {
    const id = d.account.id;
    const email = String(d.account.email);
    const status = el("p", { class: "subscribe-status" });
    const row = el("div", { class: "auth-row" });
    const buttons: [string, AdminAction, string][] = [
      ["GRANT", "grant", "auth-btn"],
      ["REVOKE", "revoke", "auth-btn auth-btn-secondary"],
      ["SIGN OUT ALL DEVICES", "revoke-tokens", "auth-btn auth-btn-secondary"],
      ["SEND RESET", "send-reset", "auth-btn auth-btn-secondary"],
      ["DELETE", "delete", "auth-btn auth-btn-danger"],
    ];
    function reset(): void {
      row.replaceChildren(
        ...buttons.map(([label, action, cls]) => {
          const btn = el("button", { class: cls }, [label]);
          btn.addEventListener("click", () => confirmStep(label, action));
          return btn;
        })
      );
    }
    function confirmStep(label: string, action: AdminAction): void {
      const confirmBtn = el("button", { class: "auth-btn auth-btn-danger" }, [`CONFIRM ${label}`]) as HTMLButtonElement;
      const cancelBtn = el("button", { class: "auth-btn auth-btn-secondary" }, ["CANCEL"]);
      cancelBtn.addEventListener("click", reset);
      const extra: HTMLElement[] = [];
      let body: () => unknown = () => undefined;
      if (action === "grant") {
        const days = el("input", { type: "number", min: "1", placeholder: "days (blank = no expiry)", class: "auth-input" }) as HTMLInputElement;
        extra.push(days);
        body = () => ({ days: days.value.trim() ? Number(days.value) : null });
      }
      if (action === "delete") {
        const typed = el("input", { type: "email", placeholder: `type ${email} to confirm`, class: "auth-input" }) as HTMLInputElement;
        confirmBtn.disabled = true;
        typed.addEventListener("input", () => {
          confirmBtn.disabled = typed.value.trim() !== email;
        });
        extra.push(typed);
      }
      confirmBtn.addEventListener("click", () => {
        confirmBtn.disabled = true;
        status.textContent = "Working…";
        adminAccountAction(id, action, body()).then(
          () => {
            status.textContent = `${label} done.`;
            if (action === "delete") navigate("/admin/accounts");
            else reload();
          },
          (err: unknown) => {
            if (denied(err)) return;
            status.textContent = describeError(err);
            reset();
          }
        );
      });
      row.replaceChildren(el("span", { class: "device-meta" }, [`${label} ${email}?`]), ...extra, confirmBtn, cancelBtn);
    }
    reset();
    return el("div", {}, [row, status]);
  }

  function showAccount(id: number): void {
    const reload = (): Promise<void> =>
      load(root, () => getAdminAccount(id), (d) => [
        labeledRows(d.account),
        ...section("Actions", actionsFor(d, () => void reload())),
        ...section("Subscriptions", buildTable(d.subscriptions ?? [])),
        ...section("Devices", buildTable(d.devices ?? [])),
        ...section("Alerts", buildTable(d.alerts ?? [])),
        ...section("Bug reports", buildTable(d.bug_reports ?? [])),
        ...section("Feedback", buildTable(d.feedback ?? [])),
        ...section("Audit", buildTable(d.audit ?? [])),
      ]);
    void reload();
  }

  function showReports(): void {
    const tabs: [string, AdminListKind][] = [
      ["BUG REPORTS", "bug-reports"],
      ["FEEDBACK", "feedback"],
    ];
    const results = el("div");
    const tabRow = el("div", { class: "timeframe-selector" });
    function show(kind: AdminListKind): void {
      for (const btn of tabRow.children) btn.classList.toggle("active", btn.getAttribute("data-kind") === kind);
      void load(results, () => listAdminItems(kind), (r) => [buildTable(r.items)]);
    }
    tabRow.append(
      ...tabs.map(([label, kind]) => {
        const btn = el("button", { class: "timeframe-btn", "data-kind": kind }, [label]);
        btn.addEventListener("click", () => show(kind));
        return btn;
      })
    );
    root.replaceChildren(tabRow, results);
    show("bug-reports");
  }

  function showList(kind: AdminListKind): void {
    void load(root, () => listAdminItems(kind), (r) => [buildTable(r.items)]);
  }

  const screens: Record<string, [title: string, back: string, show: () => void]> = {
    "/admin": ["ADMIN", "/menu", () => void load(root, () => getAdminStats(7), renderStats)],
    "/admin/accounts": ["ACCOUNTS", "/admin", showAccounts],
    "/admin/reports": ["REPORTS", "/admin", showReports],
    "/admin/errors": ["ERRORS", "/admin", () => showList("errors")],
    "/admin/audit": ["AUDIT", "/admin", () => showList("audit")],
  };

  return {
    screenFor(path: string): AdminScreen | null {
      const match = ACCOUNT_PATH.exec(path);
      const screen: [string, string, () => void] | undefined = match
        ? ["ACCOUNT", "/admin/accounts", () => showAccount(Number(match[1]))]
        : screens[path];
      if (!screen) return null;
      generation++;
      screen[2]();
      return { title: screen[0], back: screen[1], view: root };
    },
  };
}
